import { take } from 'rxjs/operators';
import {
  CheckoutSelectors,
  CompleteCheckoutSelectors,
  DirectDebitSelectors,
  PostcodeChildTicketVerifySelectors,
} from '../selectors';

import { BasketItem } from '../client/src';
import {
  CheckoutActions,
  CompleteCheckoutActions,
  DirectDebitActions,
  ImageActions,
  PostcodeChildTicketVerifyActions,
  VoucherCodeActions,
} from '../actions';
import { ICheckoutFeatureState } from '../reducers';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BookingSummary, IDirectDebitDetails } from '@nx-ng/types';
import { ImageUpload } from '../types/image-upload.interface';
import { VerifyChildPostcodeTicket } from '../types';
import { CreateBasketItem } from '../types';
import { MAX_TICKETS_IN_BASKET } from '@nx-ng/constants/max-tickets-in-basket';

@Injectable({
  providedIn: 'root',
})
export class CheckoutStoreFacade {
  /**
   * Combine pieces of state using createSelector,
   * and expose them as observables through the facade.
   */
  selectBasket$ = this.store.select(CheckoutSelectors.selectBasket);
  selectBasketId$ = this.store.select(CheckoutSelectors.selectBasketId);

  basketItemsForGtmCheckout$ = this.store.select(
    CheckoutSelectors.selectBasketItemsForGtmCheckout
  );

  selectBasketWithDescriptions$ = this.store.select(
    CheckoutSelectors.selectBasketWithDescriptions
  );
  selectBookingSummaryItems$ = this.store.select(
    CheckoutSelectors.selectBookingSummaryItems
  );
  bookingSummaryTickets$ = this.store.select(
    CheckoutSelectors.selectBookingSummaryTickets
  );

  bookingSummaryPage$ = this.store.select(
    CheckoutSelectors.selectBookingSummaryPageDetails
  );

  totalBasketQuantityCount$ = this.store.select(
    CheckoutSelectors.selectTotalBasketQuantityCount
  );

  totalBasketCount$ = this.store.select(
    CheckoutSelectors.selectTotalBasketCount
  );
  subtotalBasketPrice$ = this.store.select(
    CheckoutSelectors.selectSubtotalBasketPrice
  );
  totalBasketPrice$ = this.store.select(
    CheckoutSelectors.selectToPayBasketTotal
  );
  hasDirectDebit$ = this.store.select(CheckoutSelectors.selectHasDirectDebit);
  totalDirectDebit$ = this.store.select(
    CheckoutSelectors.selectTotalDirectDebit
  );

  allChildTicketsVerified$ = this.store.select(
    PostcodeChildTicketVerifySelectors.selectAllChildTicketsVerified
  );

  hasChildTickets$ = this.store.select(CheckoutSelectors.selectHasChildTickets);

  hasChildTicketErrors$ = this.store.select(
    PostcodeChildTicketVerifySelectors.selectHasInvalidPostcodeForChildTickets
  );

  childTicketVerifyInProgress$ = this.store.select(
    PostcodeChildTicketVerifySelectors.selectVerifyInProgress
  );

  validBasket$ = this.store.select(CheckoutSelectors.selectValidBasket);

  bankDetailsAreValid$ = this.store.select(
    DirectDebitSelectors.selectDirectDebitIsValid
  );
  bankDetails$ = this.store.select(DirectDebitSelectors.selectBankDetails);
  verifyInProgress$ = this.store.select(
    DirectDebitSelectors.selectVerifyInProgress
  );
  directDebitForm$ = this.store.select(
    DirectDebitSelectors.selectDirectDebitForm
  );

  flexiFares$ = this.store.select(
    CheckoutSelectors.selectFlexiFaresWithQuantity
  );
  paygFares$ = this.store.select(CheckoutSelectors.selectPaygFaresWithQuantity);
  searchFares$ = this.store.select(
    CheckoutSelectors.selectSearchFaresWithQuantity
  );

  isCheckoutLoading$ = this.store.select(
    CheckoutSelectors.selectCheckoutLoading
  );

  checkoutBasketRequest$ = this.store.select(
    CheckoutSelectors.selectBasketCheckoutRequest
  );

  checkoutInProgress$ = this.store.select(
    CompleteCheckoutSelectors.selectCheckoutInProgress
  );

  constructor(private readonly store: Store<ICheckoutFeatureState>) {}

  updateItemQuantityInBasket(item: CreateBasketItem) {
    this.totalBasketCount$.pipe(take(1)).subscribe((totalBasketCount) => {
      if (totalBasketCount < MAX_TICKETS_IN_BASKET || item.quantity === 0) {
        this.store.dispatch(CheckoutActions.updateBasketItemQuantity({ item }));
      } else {
        this.store.dispatch(CheckoutActions.notifyBasketIsFull());
      }
    });
  }

  applyDiscount(basketId: string, voucherCode: string) {
    this.store.dispatch(VoucherCodeActions.discountAPI_applyVoucherCode({ basketId, voucherCode }));
  }

  removeDiscount(basketId: string, voucherCode: string) {
    this.store.dispatch(VoucherCodeActions.discountAPI_removeVoucherCode({ basketId, voucherCode }));
  }

  associateUserWithBasket(basketId: string) {
    this.store.dispatch(VoucherCodeActions.discountAPI_associateUserWithBasket({ basketId }));
  }

  convertItemToSubscription(item: Pick<BasketItem, 'quantity' | 'ticketID'>) {
    this.store.dispatch(CheckoutActions.convertItemToSubscription({ item }));
  }

  loadPhoto({ basketId, itemId,  quantityIndex }: { basketId: string; itemId: string, quantityIndex: number; }) {
    this.store.dispatch(
      ImageActions.checkoutAPI_loadImage({
        basketId,
        itemId,
        quantityIndex,
      })
    );
  }

  uploadPhoto(image: ImageUpload) {
    this.store.dispatch(
      ImageActions.checkoutAPI_uploadImage({
        image,
      })
    );
  }

  loadBasket(basketId: string) {
    this.store.dispatch(
      CheckoutActions.checkoutAPI_loadBasket({
        basketId,
      })
    );
  }

  loadFareByAliasAndAddToBasket(ticketAlias: string, basketId?: string) {
    this.store.dispatch(
      CheckoutActions.addTicketToBasketUsingAlias({
        ticketAlias,
        basketId,
      })
    );
  }

  verifyDirectDebit(accountNumber?: string, sortCode?: string) {
    this.store.dispatch(
      DirectDebitActions.checkoutAPI_verifyDirectDebitDetails({
        sortCode: sortCode || '',
        accountNumber: accountNumber || '',
      })
    );
  }

  clearDirectDebitVerification() {
    this.store.dispatch(DirectDebitActions.clearDirectDebitVerfification());
  }

  saveBookingSummaryToStore(bookingSummary?: BookingSummary) {
    this.store.dispatch(
      CheckoutActions.saveBookingSummary({
        bookingSummary,
      })
    );
  }

  saveDirectDebitToStore(directDebitForm: IDirectDebitDetails) {
    this.store.dispatch(
      DirectDebitActions.saveDirectDebitForm({
        directDebitForm,
      })
    );
  }

  verifyMultipleChildTickets(verifyList: VerifyChildPostcodeTicket[]) {
    this.store.dispatch(
      PostcodeChildTicketVerifyActions.verifyPostcodeChildTickets({
        verifyList,
      })
    );
  }

  verifyChildTicket(verify: VerifyChildPostcodeTicket) {
    this.store.dispatch(
      PostcodeChildTicketVerifyActions.verifyPostcodeChildTicket({
        verify,
      })
    );
  }

  clearAllPostCodeVerifications() {
    this.store.dispatch(
      PostcodeChildTicketVerifyActions.clearAllPostcodeChildVerifications()
    );
  }

  completeBasketCheckout() {
    this.store.dispatch(CompleteCheckoutActions.checkoutBasket());
  }
}
